import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaUser, FaBars, FaTimes, FaSignOutAlt, FaBuilding, FaRoute, FaWallet, FaHome } from "react-icons/fa";
import { logout, reset } from "../features/auth/authSlice";
import { clearCorporateSession, getCorporateMember, getCorporateToken } from "../utils/corporateSession";
import LoginModal from "../components/LoginModal";
import axios from "axios";
import { API_URL } from "../config/apiConfig";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [corporateMember, setCorporateMember] = useState(null);
  const [corporateToken, setCorporateToken] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [bookingSettings, setBookingSettings] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/booking/settings/public`);
        if (res.data && res.data.success) {
          setBookingSettings(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching public settings in Header:", err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    // Initialize corporate session on component mount
    const token = getCorporateToken();
    const member = getCorporateMember();
    setCorporateMember(member);
    setCorporateToken(token);

    // Console log for debugging
    if (token && member) {
      console.log("🔄 Header: Corporate session initialized from localStorage");
    }

    const onStorage = (e) => {
      if (e.key === "corporateMember" || e.key === "corporateToken") {
        setCorporateMember(getCorporateMember());
        setCorporateToken(getCorporateToken());
      }
    };

    const onSessionChanged = () => {
      const updatedToken = getCorporateToken();
      const updatedMember = getCorporateMember();
      setCorporateMember(updatedMember);
      setCorporateToken(updatedToken);
      console.log("🔄 Header: Corporate session changed");
    };

    // Listen to restoration event on app load
    const onSessionRestored = () => {
      const token = getCorporateToken();
      const member = getCorporateMember();
      if (token && member) {
        setCorporateMember(member);
        setCorporateToken(token);
        console.log("✅ Header: Corporate session restored");
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("corporateSessionChanged", onSessionChanged);
    window.addEventListener("corporateSessionRestored", onSessionRestored);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("corporateSessionChanged", onSessionChanged);
      window.removeEventListener("corporateSessionRestored", onSessionRestored);
    };
  }, []);

  // Never show corporate menu on login/register pages
  const isOnAuthPage =
    location.pathname === "/corporate/login" ||
    location.pathname === "/corporate/register" ||
    location.pathname === "/login" ||
    location.pathname === "/verify-otp";

  const corporateLoggedIn = !!corporateToken && !!corporateMember && !isOnAuthPage;
  const corporateDisplayName = (() => {
    const raw = corporateMember?.fullName?.trim() || corporateMember?.name?.trim() || corporateMember?.email || corporateMember?.mobileNumber || "Corporate";
    return String(raw).trim().split(" ")[0] || "Corporate";
  })();

  // Handle scroll event to change header style
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const onLogout = () => {
    dispatch(logout());
    dispatch(reset());
    navigate("/");
  };

  const onCorporateLogout = () => {
    clearCorporateSession();
    sessionStorage.removeItem("corporateBookingMode");
    window.dispatchEvent(new Event("corporateSessionChanged"));
    navigate("/");
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      try {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } catch (err) {
        window.scrollTo(0, 0);
      }
    } else {
      try {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } catch (err) {
        window.scrollTo(0, 0);
      }
    }
  };

 const getLinkClassName = (path) => {
  const activeColor = corporateLoggedIn ? "text-[#F56753]" : "text-blue-600";
  const hoverColor = corporateLoggedIn ? "hover:text-[#F56753]" : "hover:text-blue-600";
  const underlineColor = corporateLoggedIn ? "after:bg-[#F56753]" : "after:bg-blue-600";

  return `relative font-semibold text-gray-800 px-2 py-1 transition
    ${hoverColor}
    after:absolute after:left-0 after:-bottom-1
    after:h-[2px] after:w-0
    ${underlineColor}
    after:transition-all after:duration-300
    hover:after:w-full
    ${isActive(path) ? `${activeColor} after:w-full` : ""}`;
};

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 bg-white shadow-md min-h-[44px] md:min-h-[60px] ${isScrolled ? "py-1 md:py-1.5" : "py-1.5 md:py-2.5"} ${location.pathname.toLowerCase().startsWith('/cabs') || location.pathname.toLowerCase().startsWith('/booking/') ? 'hidden md:block' : ''}`}
    >
      <div className="max-w-[1180px] mx-auto px-6 flex items-center justify-between h-full">
        {/* Logo */}
        <Link to="/" onClick={handleLogoClick} className="flex items-center z-10 relative flex-shrink-0 cursor-pointer" title="Go to top">
          <span className="text-xl md:text-2xl lg:text-3xl font-bold whitespace-nowrap leading-none flex items-center h-full">
            <span className="text-blue-600">Cab</span><span className="text-orange-500">Zigo</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
       <nav className="hidden md:flex items-center space-x-6 ml-auto">
          <Link to="/" onClick={handleLogoClick} className={getLinkClassName("/")}>
            Home
          </Link>
          <Link to="/about" className={getLinkClassName("/about")}>
            About Us
          </Link>
          {(!bookingSettings || bookingSettings.enableAdminDriverModule !== false) && (
            <Link to="/drivers" className={getLinkClassName("/drivers")}>
              Drivers
            </Link>
          )}
          <Link to="/blogs" className={getLinkClassName("/blogs")}>
            Blog
          </Link>
        </nav>

        {/* Right Side - Login Button or User Menu */}
        <div className="hidden md:flex items-center ml-6">
          {user ? (
            <div className="relative overflow-visible">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-full"
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                  {user.user?.image || user.data?.image ? (
                    <img 
                      src={user.user?.image || user.data?.image} 
                      alt={user.user?.name || user.data?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaUser className="text-blue-500 text-sm" />
                  )}
                </div>
                <span className="text-sm font-medium truncate max-w-[80px] lg:max-w-[120px]">
                  {user.user?.name ? user.user.name.split(" ")[0] : user.data?.name ? user.data.name.split(" ")[0] : "User"}
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-[200px] bg-white rounded-xl shadow-xl py-3 z-[100] border border-gray-100">
                  {/* Menu Items */}
                  <div className="py-2">
                    {(user.user?.role === "admin" || user.data?.role === "admin" || user?.role === "admin") && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition font-bold border-b border-gray-50 bg-blue-50/30"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/dashboard"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Dashboard
                    </Link>
                    <Link
                      to="/bookings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      My Bookings
                    </Link>

                    <Link
                      to="/wallet"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      My Wallet
                    </Link>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        onLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-orange-500 hover:bg-gray-50 transition font-medium"
                    >
                      <FaSignOutAlt className="w-4 h-4" />
                      LogOut
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : corporateLoggedIn ? (
            <div className="relative overflow-visible">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-2 text-gray-700 hover:text-[#F56753] transition bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-full"
              >
                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center overflow-hidden">
                  {corporateMember?.profileImageUrl ? (
                    <img src={corporateMember.profileImageUrl} alt={corporateDisplayName} className="w-full h-full object-cover" />
                  ) : (
                    <FaUser className="text-[#F56753] text-sm" />
                  )}
                </div>
                <span className="text-sm font-medium truncate max-w-[80px] lg:max-w-[120px]">
                  {corporateDisplayName}
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl py-2.5 z-[100] border border-gray-150 animate-in fade-in-50 slide-in-from-top-2 duration-150 text-left">
                  {/* User profile card block */}
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 rounded-t-2xl mb-1">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-[#F56753] font-extrabold text-xs uppercase overflow-hidden">
                        {corporateMember?.profileImageUrl ? (
                          <img src={corporateMember.profileImageUrl} alt={corporateMember?.fullName} className="w-full h-full object-cover" />
                        ) : (
                          corporateMember?.fullName ? corporateMember.fullName.charAt(0).toUpperCase() : 'C'
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-extrabold text-slate-900 truncate">
                          {corporateMember?.fullName || 'Corporate User'}
                        </p>
                        <span className="inline-block text-[9px] font-bold text-[#F56753] bg-orange-50 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                          {corporateMember?.role === 'admin' ? 'Admin' : 'Staff'}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs font-semibold text-slate-600 truncate">
                      {corporateMember?.companyName || ''}
                    </p>
                    <p className="text-[11px] font-medium text-slate-400 truncate">
                      {corporateMember?.email || ''}
                    </p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    {corporateMember?.role === 'admin' ? (
                      <>
                        {/* Cabzigo Admin Header */}
                        <div className="px-4 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1 border-b border-slate-50 mb-1">
                          <span className="text-[#F56753]">Cab</span>
                          <span className="text-slate-800 font-extrabold">Zigo</span>
                          <span className="ml-1 text-[9px] font-bold text-slate-400 border border-slate-200 px-1 py-0.2 rounded uppercase tracking-wider bg-slate-50">Admin</span>
                        </div>
                        <Link
                          to="/corporate/admin-dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                          </svg>
                          <span>Dashboard</span>
                        </Link>
                        <Link
                          to="/corporate/staff"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FaBuilding className="text-slate-400 text-xs w-4 h-4" />
                          <span>Company & Employees</span>
                        </Link>
                        <Link
                          to="/corporate/bookings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 002-2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Bookings</span>
                        </Link>
                        <Link
                          to="/corporate/wallet"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FaWallet className="text-slate-400 text-xs w-4 h-4" />
                          <span>Cabzigo Wallet</span>
                        </Link>
                        <Link
                          to="/corporate/pending-approvals"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FaRoute className="text-slate-400 text-xs w-4 h-4" />
                          <span>Travel Management</span>
                        </Link>
                        <Link
                          to="/corporate/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FaHome className="text-slate-400 text-xs w-4 h-4" />
                          <span>Cabzigo Home</span>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                          </svg>
                          <span>Dashboard</span>
                        </Link>
                        <Link
                          to="/bookings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>My Bookings</span>
                        </Link>
                        <Link
                          to="/wallet"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[#F56753] hover:bg-orange-50/50 transition w-full"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FaWallet className="text-slate-400 text-xs w-4 h-4" />
                          <span>My Wallet</span>
                        </Link>
                      </>
                    )}
                  </div>

                  {/* Logout */}
                  <div className="border-t border-slate-100 mt-1.5 pt-1.5">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        onCorporateLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm font-bold text-orange-500 hover:bg-red-50/50 transition"
                    >
                      <FaSignOutAlt className="w-4 h-4 text-orange-400" />
                      <span>LogOut</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {!corporateToken && (
                <>
                  <Link
                    to="/corporate/login"
                    className="bg-gray-900 text-white py-2 px-4 rounded-md hover:bg-gray-800 transition whitespace-nowrap font-medium text-sm"
                  >
                    Corporate
                  </Link>
                  <button
                    onClick={() => setIsLoginModalOpen(true)}
                    className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition whitespace-nowrap font-medium text-sm flex items-center gap-2"
                  >
                    <FaUser /> LOGIN
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-700 text-xl p-1 hover:text-primary transition"
          onClick={toggleMenu}
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white shadow-lg py-4 absolute left-0 right-0 top-full z-50 overflow-y-auto max-h-[calc(100vh-5rem)] border-t border-gray-200">
          <nav className="w-full px-4 md:px-6 lg:px-8 xl:px-12 flex flex-col space-y-3">
            <Link
              to="/"
              className={`${getLinkClassName("/")} border-b border-gray-100`}
              onClick={(e) => { toggleMenu(); handleLogoClick(e); }}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`${getLinkClassName("/about")} border-b border-gray-100`}
              onClick={toggleMenu}
            >
              About Us
            </Link>
            {(!bookingSettings || bookingSettings.enableAdminDriverModule !== false) && (
              <Link
                to="/drivers"
                className={`${getLinkClassName("/drivers")} border-b border-gray-100`}
                onClick={toggleMenu}
              >
                Drivers
              </Link>
            )}
            <Link
              to="/blogs"
              className={`${getLinkClassName("/blogs")} border-b border-gray-100`}
              onClick={toggleMenu}
            >
              Blog
            </Link>

            {!corporateLoggedIn && (
              <Link
                to={corporateToken ? "/corporate/dashboard" : "/corporate/login"}
                className="text-gray-700 hover:text-blue-600 transition py-2 border-b border-gray-100"
                onClick={toggleMenu}
              >
                Corporate
              </Link>
            )}

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-blue-600 transition py-2 border-b border-gray-100"
                  onClick={toggleMenu}
                >
                  Dashboard
                </Link>
                <Link
                  to="/bookings"
                  className="text-gray-700 hover:text-blue-600 transition py-2 border-b border-gray-100"
                  onClick={toggleMenu}
                >
                  My Bookings
                </Link>

                <Link
                  to="/wallet"
                  className="text-gray-700 hover:text-blue-600 transition py-2 border-b border-gray-100"
                  onClick={toggleMenu}
                >
                  Wallet
                </Link>
                {(user.user?.role === "admin" || user.data?.role === "admin" || user?.role === "admin") && (
                  <Link
                    to="/admin"
                    className="text-blue-600 font-bold hover:text-blue-700 transition py-2 border-b border-gray-100 flex items-center gap-2"
                    onClick={toggleMenu}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => {
                    toggleMenu();
                    onLogout();
                  }}
                  className="flex items-center text-gray-700 hover:text-blue-600 transition py-2 w-full"
                >
                  <FaSignOutAlt className="mr-2" /> Logout
                </button>
              </>
            ) : corporateLoggedIn ? (
              <>
                {corporateMember?.role === 'admin' ? (
                  <>
                    <Link
                      to="/corporate/dashboard"
                      className={`text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 border-b border-gray-100`}
                      onClick={toggleMenu}
                    >
                      Admin Panel
                    </Link>
                    <Link
                      to="/corporate/bookings"
                      className={`text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 border-b border-gray-100`}
                      onClick={toggleMenu}
                    >
                      Bookings
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/dashboard"
                      className={`text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 border-b border-gray-100`}
                      onClick={toggleMenu}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/bookings"
                      className={`text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 border-b border-gray-100`}
                      onClick={toggleMenu}
                    >
                      My Bookings
                    </Link>

                    <Link
                      to="/wallet"
                      className={`text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 border-b border-gray-100`}
                      onClick={toggleMenu}
                    >
                      My Wallet
                    </Link>
                  </>
                )}
                <button
                  onClick={() => {
                    toggleMenu();
                    onCorporateLogout();
                  }}
                  className={`flex items-center text-gray-700 ${corporateLoggedIn ? 'hover:text-[#F56753]' : 'hover:text-blue-600'} transition py-2 w-full`}
                >
                  <FaSignOutAlt className="mr-2" /> Logout
                </button>
              </>
            ) : (
              <div className="space-y-2 pt-2">
                <button
                  onClick={() => {
                    setIsLoginModalOpen(true);
                    toggleMenu();
                  }}
                  className="bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition text-center block font-medium flex items-center justify-center gap-2 w-full"
                >
                  <FaUser /> LOGIN
                </button>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-2 px-2">Quick Access:</p>
                  {(!bookingSettings || bookingSettings.enableAdminDriverModule !== false) && (
                    <Link
                      to="/driver/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                      onClick={toggleMenu}
                    >
                      Driver Login
                    </Link>
                  )}
                  {(!bookingSettings || bookingSettings.enableVendorModule !== false) && (
                    <Link
                      to="/vendor/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                      onClick={toggleMenu}
                    >
                      Vendor Login
                    </Link>
                  )}
                </div>
              </div>
            )}
          </nav>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </header>
  );
};

export default Header;
